/**
 * Поток микрофона, открытый прямо в касании.
 *
 * iOS (и строгие браузеры) пускают к микрофону и звуку только внутри жеста:
 * `getUserMedia` после `await` (проверка разрешения, ответ сервера) может
 * получить `NotAllowedError`, а AudioContext — остаться «suspended».
 * Поэтому кнопка микрофона первым же синхронным действием зовёт
 * `primeMicrophone()`, а запись потом забирает этот поток через
 * `takeMicStream()` — второго запроса нет. Незабранный поток через
 * `PRIME_TTL_MS` закрывается: индикатор микрофона не горит зря.
 */

import { prepareCaptureSession, trackCapture } from '../lib/audioSession';
import { reportMicFailure } from './micDiagnostics';

/** Одни настройки звука для всех путей записи (поток, запись на сервер). */
export const MIC_CONSTRAINTS: MediaStreamConstraints = {
  audio: { channelCount: 1, echoCancellation: true, noiseSuppression: true, autoGainControl: true },
};

const PRIME_TTL_MS = 15_000;

function isSessionConflict(error: unknown): boolean {
  return typeof error === 'object' && error !== null && (error as { name?: unknown }).name === 'InvalidStateError';
}

/**
 * `getUserMedia` с правильной аудиосессией Safari. Первый запрос уходит
 * синхронно (в жесте). Если сессию между делом перевели на `playback`
 * (`InvalidStateError`), переводим обратно и повторяем один раз.
 */
function requestMic(): Promise<MediaStream> {
  prepareCaptureSession();
  let first: Promise<MediaStream>;
  try {
    first = navigator.mediaDevices.getUserMedia(MIC_CONSTRAINTS);
  } catch (error) {
    first = Promise.reject(error instanceof Error ? error : new Error(String(error)));
  }
  return first.then(trackCapture, (error: unknown) => {
    if (!isSessionConflict(error)) throw error;
    prepareCaptureSession();
    return navigator.mediaDevices.getUserMedia(MIC_CONSTRAINTS).then(trackCapture);
  });
}

interface Primed {
  readonly promise: Promise<MediaStream>;
  readonly at: number;
}

let primed: Primed | null = null;

function stopStream(stream: MediaStream): void {
  stream.getTracks().forEach((track) => track.stop());
}

function live(stream: MediaStream): boolean {
  return stream.getAudioTracks().some((track) => track.readyState === 'live');
}

/**
 * Открыть микрофон сейчас, внутри жеста. Звать синхронно, первым действием
 * в обработчике касания. Повторный вызов, пока поток свежий, ничего не делает.
 */
export function primeMicrophone(): Promise<MediaStream> | null {
  if (typeof navigator === 'undefined' || typeof navigator.mediaDevices?.getUserMedia !== 'function') return null;
  if (primed !== null && Date.now() - primed.at < PRIME_TTL_MS) return primed.promise;
  const promise = requestMic();
  const entry: Primed = { promise, at: Date.now() };
  primed = entry;
  promise.then(
    (stream) => {
      window.setTimeout(() => {
        // Никто не забрал — закрываем.
        if (primed === entry) {
          primed = null;
          stopStream(stream);
        }
      }, PRIME_TTL_MS);
    },
    (error: unknown) => {
      reportMicFailure('prime', error);
      if (primed === entry) primed = null;
    },
  );
  return promise;
}

/** Результат открытия в касании, если оно было и ещё свежее (не забирает поток). */
export function primedMicrophone(): Promise<MediaStream> | null {
  return primed !== null && Date.now() - primed.at < PRIME_TTL_MS ? primed.promise : null;
}

/**
 * Поток для записи: открытый в касании, если он есть, иначе — новый запрос.
 * Ошибка — как у `getUserMedia` (`DOMException`).
 */
export async function takeMicStream(): Promise<MediaStream> {
  const entry = primed;
  if (entry !== null && Date.now() - entry.at < PRIME_TTL_MS) {
    primed = null;
    try {
      const stream = await entry.promise;
      if (live(stream)) return stream;
      stopStream(stream);
    } catch {
      // Открытие в касании не удалось — пробуем ещё раз ниже: причина уже записана.
    }
  }
  return requestMic();
}

/** Отпустить открытый в касании поток — системному распознаванию (Web Speech) микрофон нужен свободным. */
export function releasePrimedMicrophone(): void {
  const entry = primed;
  primed = null;
  entry?.promise.then(stopStream, () => undefined);
}
